import { Fragment, useEffect, useRef, useState, type ReactNode } from 'react'
import { Braces, Check, Loader2, Mail, RotateCcw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'
import {
  EMAIL_TEMPLATE_BODY_MAX,
  EMAIL_TEMPLATE_SUBJECT_MAX,
  type EmailTemplatePlaceholder,
} from '@/api/emailTemplatesApi'
import { useOosEmailTemplate, useSaveOosEmailTemplate } from '@/hooks/useOosEmailTemplate'
import { usePermission, PERMISSIONS } from '@/lib/permissions'
import { useTenant } from '@/providers/TenantProvider'

const errorText = (e: unknown) => (e instanceof Error && e.message ? e.message : 'Something went wrong.')

const TOKEN = /\{\{([a-z0-9_]+)\}\}/g

/** What the preview shows for each detail. Made up, so no real record shows. */
const SAMPLE: Record<string, string> = {
  form_name: 'Processing',
  field_label: 'Viability (%)',
  value: '85',
  severity: 'Low',
  entered_by: 'jane.doe@example.org',
  entered_at: 'Oct 14, 2026, 9:05 AM',
  link: 'https://app.smart-cgt.com/notifications',
}

/**
 * Configure → Email Groups: the email sent to a field's alert groups when a
 * value is out of specification and the field's email switch is on
 * (z8nrz7eq4h). One template per tenant; the server holds the default.
 *
 * Laid out as the email itself: a compose pane on the left and, on the
 * right, the message as a group member receives it, with sample details in
 * place of the placeholders. A placeholder the server does not know shows in
 * red there, before Save refuses it.
 */
export function OosEmailTemplateCard() {
  const { can } = usePermission()
  const canEdit = can(PERMISSIONS.usersManage)
  const { selectedTenant } = useTenant()
  const template = useOosEmailTemplate()
  const { save, reset } = useSaveOosEmailTemplate()

  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [target, setTarget] = useState<'subject' | 'body'>('body')
  const [insertOpen, setInsertOpen] = useState(false)
  const [justSaved, setJustSaved] = useState(false)
  const subjectRef = useRef<HTMLInputElement>(null)
  const bodyRef = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    if (!template.data) return
    setSubject(template.data.subject)
    setBody(template.data.body)
  }, [template.data])

  useEffect(() => {
    if (!justSaved) return
    const t = setTimeout(() => setJustSaved(false), 2000)
    return () => clearTimeout(t)
  }, [justSaved])

  const edited = () => {
    save.reset()
    setJustSaved(false)
  }

  const insertToken = (token: string) => {
    const text = `{{${token}}}`
    const isSubject = target === 'subject'
    const el = isSubject ? subjectRef.current : bodyRef.current
    const current = isSubject ? subject : body
    const start = el?.selectionStart ?? current.length
    const end = el?.selectionEnd ?? current.length
    const next = current.slice(0, start) + text + current.slice(end)
    if (isSubject) setSubject(next)
    else setBody(next)
    edited()
    setInsertOpen(false)
    // Put the cursor after the detail once React has written the new value.
    requestAnimationFrame(() => {
      if (!el) return
      el.focus()
      el.setSelectionRange(start + text.length, start + text.length)
    })
  }

  const data = template.data
  const dirty = !!data && (subject !== data.subject || body !== data.body)
  const busy = save.isPending || reset.isPending
  const error = save.error ?? reset.error
  const samples: Record<string, string> = { ...SAMPLE, tenant_name: selectedTenant?.name ?? 'Your site' }
  const known = new Set((data?.placeholders ?? []).map(p => p.token))

  return (
    <Card className='overflow-hidden'>
      <div className='flex flex-wrap items-start justify-between gap-3 border-b bg-muted/30 px-5 py-4'>
        <div className='flex items-start gap-3'>
          <div className='flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary'>
            <Mail className='h-4 w-4' />
          </div>
          <div>
            <div className='flex items-center gap-2'>
              <p className='text-sm font-semibold'>Out-of-specification alert</p>
              {data && (
                <span
                  className={cn(
                    'rounded-full px-2 py-0.5 text-[11px] font-medium',
                    data.isDefault ? 'bg-muted text-muted-foreground' : 'bg-primary/10 text-primary',
                  )}
                >
                  {data.isDefault ? 'Default' : 'Custom'}
                </span>
              )}
            </div>
            <p className='mt-0.5 text-xs text-muted-foreground'>
              Sent to the groups when a value is out of specification and the field&apos;s email switch is on.
            </p>
          </div>
        </div>
        {data && !data.isDefault && (
          <p className='text-xs text-muted-foreground'>
            Changed{data.updatedBy ? ` by ${data.updatedBy}` : ''}
            {data.updatedAt ? ` on ${new Date(data.updatedAt).toLocaleString()}` : ''}
          </p>
        )}
      </div>

      <CardContent className='p-5'>
        {template.isLoading && <Loader2 className='h-4 w-4 animate-spin text-muted-foreground' />}
        {template.error && <p className='text-sm text-destructive'>{errorText(template.error)}</p>}

        {data && (
          <div className='grid gap-5 lg:grid-cols-2'>
            {/* Compose */}
            <div className='flex flex-col'>
              <p className='mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground'>Write</p>
              <div
                className={cn(
                  'flex flex-1 flex-col rounded-lg border bg-background',
                  canEdit && 'focus-within:ring-2 focus-within:ring-ring/40',
                )}
              >
                <div className='flex items-center gap-3 border-b px-3 py-2'>
                  <label htmlFor='oos-email-subject' className='w-16 shrink-0 text-xs text-muted-foreground'>
                    Subject
                  </label>
                  <input
                    id='oos-email-subject'
                    ref={subjectRef}
                    value={subject}
                    maxLength={EMAIL_TEMPLATE_SUBJECT_MAX}
                    disabled={!canEdit}
                    onFocus={() => setTarget('subject')}
                    onChange={e => {
                      setSubject(e.target.value)
                      edited()
                    }}
                    className='min-w-0 flex-1 bg-transparent text-sm font-medium outline-none disabled:opacity-70'
                  />
                </div>
                <Textarea
                  id='oos-email-body'
                  aria-label='Message'
                  ref={bodyRef}
                  value={body}
                  rows={11}
                  maxLength={EMAIL_TEMPLATE_BODY_MAX}
                  disabled={!canEdit}
                  onFocus={() => setTarget('body')}
                  onChange={e => {
                    setBody(e.target.value)
                    edited()
                  }}
                  className='flex-1 resize-none rounded-none border-0 font-mono text-[13px] leading-relaxed shadow-none focus-visible:ring-0'
                />
                {canEdit && (
                  <div className='flex items-center justify-between gap-2 border-t bg-muted/20 px-2 py-1.5'>
                    <Popover open={insertOpen} onOpenChange={setInsertOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          type='button'
                          variant='ghost'
                          size='sm'
                          className='h-7 gap-1.5 text-xs'
                          // Keep the cursor where it is in the box being edited.
                          onMouseDown={e => e.preventDefault()}
                        >
                          <Braces className='h-3.5 w-3.5' />
                          Insert detail
                          <span className='text-muted-foreground'>
                            into {target === 'subject' ? 'subject' : 'message'}
                          </span>
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent
                        align='start'
                        className='w-64 p-1'
                        onOpenAutoFocus={e => e.preventDefault()}
                      >
                        {data.placeholders.map(p => (
                          <PlaceholderRow key={p.token} placeholder={p} sample={samples[p.token]} onPick={insertToken} />
                        ))}
                      </PopoverContent>
                    </Popover>
                    <span className='pr-1 text-[11px] tabular-nums text-muted-foreground'>
                      {body.length} / {EMAIL_TEMPLATE_BODY_MAX}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Preview */}
            <div className='flex flex-col'>
              <p className='mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground'>
                What the group receives
              </p>
              <div className='flex flex-1 flex-col rounded-lg border bg-muted/30 p-3'>
                <div className='flex-1 rounded-md bg-background shadow-sm'>
                  <div className='space-y-1 border-b px-4 py-3 text-xs text-muted-foreground'>
                    <p>
                      <span className='inline-block w-12'>From</span>
                      <span className='text-foreground'>notifications@smart-cgt.com</span>
                    </p>
                    <p>
                      <span className='inline-block w-12'>To</span>
                      <span className='text-foreground'>Members of the alerted groups</span>
                    </p>
                    <p className='pt-1 text-sm font-semibold text-foreground'>
                      <Filled text={subject.replace(/[\r\n]+/g, ' ')} samples={samples} known={known} />
                    </p>
                  </div>
                  <div className='whitespace-pre-wrap break-words px-4 py-3 text-sm leading-relaxed'>
                    <Filled text={body} samples={samples} known={known} />
                  </div>
                </div>
                <p className='mt-2 text-[11px] text-muted-foreground'>
                  Sample details shown. Highlighted text is filled in for each alert.
                </p>
              </div>
            </div>
          </div>
        )}

        {data && (error || canEdit) && (
          <div className='mt-5 flex flex-wrap items-center justify-between gap-3 border-t pt-4'>
            <div className='min-h-5 text-sm'>
              {error ? (
                <span className='text-destructive'>{errorText(error)}</span>
              ) : justSaved ? (
                <span className='inline-flex items-center gap-1.5 text-emerald-600'>
                  <Check className='h-4 w-4' />
                  Saved
                </span>
              ) : dirty ? (
                <span className='inline-flex items-center gap-1.5 text-muted-foreground'>
                  <span className='h-1.5 w-1.5 rounded-full bg-amber-500' />
                  Unsaved changes
                </span>
              ) : null}
            </div>
            {canEdit && (
              <div className='flex gap-2'>
                {dirty && (
                  <Button
                    variant='ghost'
                    size='sm'
                    disabled={busy}
                    onClick={() => {
                      setSubject(data.subject)
                      setBody(data.body)
                      edited()
                    }}
                  >
                    Discard
                  </Button>
                )}
                <Button
                  variant='outline'
                  size='sm'
                  className='gap-1.5'
                  disabled={busy || data.isDefault}
                  onClick={() => {
                    save.reset()
                    reset.mutate(undefined, { onSuccess: () => setJustSaved(true) })
                  }}
                >
                  {reset.isPending ? (
                    <Loader2 className='h-3.5 w-3.5 animate-spin' />
                  ) : (
                    <RotateCcw className='h-3.5 w-3.5' />
                  )}
                  Use default
                </Button>
                <Button
                  size='sm'
                  disabled={busy || !dirty || !subject.trim() || !body.trim()}
                  onClick={() => {
                    reset.reset()
                    save.mutate({ subject, body }, { onSuccess: () => setJustSaved(true) })
                  }}
                >
                  {save.isPending && <Loader2 className='mr-1.5 h-3.5 w-3.5 animate-spin' />}
                  Save
                </Button>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

function PlaceholderRow({
  placeholder,
  sample,
  onPick,
}: {
  placeholder: EmailTemplatePlaceholder
  sample: string | undefined
  onPick: (token: string) => void
}) {
  return (
    <button
      type='button'
      onMouseDown={e => e.preventDefault()}
      onClick={() => onPick(placeholder.token)}
      className='flex w-full flex-col items-start rounded-md px-2.5 py-1.5 text-left hover:bg-accent'
    >
      <span className='text-sm'>{placeholder.label}</span>
      {sample && <span className='max-w-full truncate text-[11px] text-muted-foreground'>e.g. {sample}</span>}
    </button>
  )
}

/** `text` with each placeholder the server knows replaced by its sample, highlighted; unknown ones in red. */
function Filled({
  text,
  samples,
  known,
}: {
  text: string
  samples: Record<string, string>
  known: ReadonlySet<string>
}) {
  if (!text.trim()) return <span className='italic text-muted-foreground'>Empty</span>
  const parts: ReactNode[] = []
  let last = 0
  for (const m of text.matchAll(TOKEN)) {
    const at = m.index ?? 0
    if (at > last) parts.push(text.slice(last, at))
    // The server's placeholder list decides what is known; samples only dress it.
    const value = samples[m[1]] ?? m[0]
    parts.push(
      !known.has(m[1]) ? (
        <span key={at} className='rounded bg-destructive/10 px-0.5 text-destructive' title='Not a detail the email can fill'>
          {m[0]}
        </span>
      ) : m[1] === 'link' ? (
        <span key={at} className='text-primary underline underline-offset-2'>
          {value}
        </span>
      ) : (
        <span key={at} className='rounded bg-primary/10 px-0.5 text-primary'>
          {value}
        </span>
      ),
    )
    last = at + m[0].length
  }
  if (last < text.length) parts.push(text.slice(last))
  return <>{parts.map((p, i) => <Fragment key={i}>{p}</Fragment>)}</>
}
